import React, { Suspense, useEffect, useState, lazy } from "react";

// Libraries
import { Routes, Route, useLocation } from "react-router-dom";
import retina from "retinajs";
import { AnimatePresence } from "framer-motion";
import "react-quill/dist/quill.snow.css";


// Context
import GlobalContext from "./Context/Context";

// Components
import ScrollToTopButton from "./Components/ScrollToTop";
import NewsDetail from "./Components/News/NewsDetail";
import ForgotPassword from "./Pages/ForgotPassword";
import ResetPassword from "./Pages/ResetPassword";
import ManageAdjudicators from "./Pages/ManageAdjudicators";
import ManageCases from "./Pages/ManageCases";
import Statistics from "./Pages/Statistics";
import ManageNewsletter from "./Pages/ManageNewsletter";
import CasePage from "./Pages/CasePage";
import SubscriptionConfirmation from "./Pages/SubscriptionConfirmation";
import ManageHelp from "./Pages/ManageHelp";
import DecisionSummary from "./Pages/DecisionSummary";


// Home

const Home = lazy(() => import("./Pages/Home"));
const Adjudicators = lazy(() => import("./Pages/Adjudicators"));
const News = lazy(() => import("./Pages/News"));
const Cases = lazy(() => import("./Pages/Cases"));
// const Statistics = lazy(() => import("./Pages/Statistics"));
const Contact = lazy(() => import("./Pages/Contact"));
// const FilingADispute = lazy(() => import("./Pages/FilingADispute"));
// const FilingAResponse = lazy(() => import("./Pages/FilingAResponse"));
// const Fees = lazy(() => import("./Pages/Fees"));
// const FinancialAssistance = lazy(() => import("./Pages/FinancialAssistance"));
// const FlowChart = lazy(() => import("./Pages/FlowChart"));
// const GeneralAdvisory = lazy(() => import("./Pages/GeneralAdvisory"));
// const Presentations = lazy(() => import("./Pages/Presentations"));
// const Regulations = lazy(() => import("./Pages/Regulations"));
// const SupplementaryProcedures = lazy(() => import("./Pages/SupplementaryProcedures"));
// const Resources = lazy(() => import("./Pages/Resources"));
const Login = lazy(() => import("./Pages/Login"));
const Register = lazy(() => import("./Pages/Register"));
const UserProfile = lazy(() => import("./Pages/UserProfile"));
const CaseProfile = lazy(() => import("./Pages/CaseProfile"));
const DisputeFaqs = lazy(() => import("./Pages/DisputeFaqs"));
const FaqDetail = lazy(() => import("./Pages/FaqDetail"));
const FaqCategory = lazy(() => import("./Pages/FaqCategory"));
// const ResourcesContent = lazy(() => import("./Pages/ResourcesContent"));
const GenericContent = lazy(() => import("./Pages/GenericContent"));
const SearchResults = lazy(() => import("./Pages/SearchResults"));
const DDAdmin = lazy(() => import("./Pages/DDAdmin"));
const ManageContent = lazy(() => import("./Pages/ManageContent"));
const ManageNews = lazy(() => import("./Pages/ManageNews"));
const ManageFirms = lazy(() => import("./Pages/ManageFirms"));
const ManageNotices = lazy(() => import("./Pages/ManageNotices"));
const ManageAccess = lazy(() => import("./Pages/ManageAccess"));


function App() {


  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customModal, setCustomModal] = useState({
    el: null,
    isOpen: false,
  });
  const location = useLocation();

  // RetinaJS
  useEffect(() => {
    window.addEventListener("load", retina(document.querySelectorAll("img")));
  }, []);

  useEffect(() => {
    setTimeout(() => {
      import("./Functions/Utilities").then((module) => {
        module.SetHeaderMenuPos();
        module.setDocumentFullHeight();
      });
    }, 1000);
  }, [location]);

  useEffect(() => {
    if (isModalOpen === true) {
      document.querySelector("body").classList.add("overflow-hidden");
    } else {
      document.querySelector("body").classList.remove("overflow-hidden");
    }
  }, [isModalOpen]);

  // Get the current location and set the window to top
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
    setFooterHeight(0);
    setCustomModal({
      ...customModal,
      el: null,
      isOpen: false,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <GlobalContext.Provider
      value={{
        headerHeight,
        setHeaderHeight,
        footerHeight,
        setFooterHeight,
        isModalOpen,
        setIsModalOpen,
        customModal,
        setCustomModal,
      }}
    >
      <div className="App" style={{ "--header-height": `${headerHeight}px` }}>
        {
          <main style={{ marginTop: headerHeight, marginBottom: footerHeight }}>
            <ScrollToTopButton />
            <AnimatePresence mode="wait">
              <Suspense fallback={<></>}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/adjudicators" element={<Adjudicators />} />
                  <Route path="/news" element={<News />} />
                  <Route path="/cases" element={<Cases />} />
                  {/* <Route path="/statistics" element={<Statistics />} /> */}
                  <Route path="/contact" element={<Contact />} />
                  {/* <Route path="/resources" element={<Resources />} /> */}
                  {/* <Route path="/filing-a-dispute" element={<FilingADispute />} /> */}
                  {/* <Route path="/filing-a-response" element={<FilingAResponse />} /> */}
                  {/* <Route path="/fees" element={<Fees />} /> */}
                  {/* <Route path="/financial-assistance" element={<FinancialAssistance />} /> */}
                  {/* <Route path="/flow-chart" element={<FlowChart />} /> */}
                  {/* <Route path="/general-advisory" element={<GeneralAdvisory />} />
                  <Route path="/presentations" element={<Presentations />} />
                  <Route path="/regulations" element={<Regulations />} />
                  <Route path="/supplementary-procedures" element={<SupplementaryProcedures />} /> */}
                  {/* <Route path="/resources" element={<ResourcesContent />} /> */}
                  <Route path="/content/:contentid" element={<GenericContent />} />
                  <Route path="/news/:id" element={<NewsDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/userprofile" element={<UserProfile />} />
                  <Route path="/profile/:caseid" element={<CaseProfile />} />
                  <Route path="/faqs" element={<DisputeFaqs />} />
                  <Route path="/faqs/:faqcatid/:faqid" element={<FaqDetail />} />
                  <Route path="/faqs/:faqcatid/" element={<FaqCategory />} />
                  <Route path="/search/:query/" element={<SearchResults />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />
                  <Route path="/ddadmin" element={<DDAdmin />} />
                  <Route path="/ddadmin/content" element={<ManageContent />} />
                  <Route path="/ddadmin/adjudicators" element={<ManageAdjudicators />} />
                  <Route path="/ddadmin/news" element={<ManageNews />} />
                  <Route path="/ddadmin/firms" element={<ManageFirms />} />
                  <Route path="/ddadmin/notices" element={<ManageNotices />} />
                  <Route path="/ddadmin/cases" element={<ManageCases />} />
                  <Route path="/ddadmin/newsletter" element={<ManageNewsletter />} />
                  <Route path="/ddadmin/help" element={<ManageHelp />} />
                  <Route path="/ddadmin/access" element={<ManageAccess />} />
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/cases/:caseid" element={<CasePage />} />
                  <Route path="/subscribe-confirm/:email" element={<SubscriptionConfirmation/>} />
                  <Route path="/summaries/:caseid" element={<DecisionSummary />} />
                </Routes>
              </Suspense>
            </AnimatePresence>
          </main>
        }
      </div>
    </GlobalContext.Provider>
  );
}

export default App;
